import React, { useEffect, useState } from 'react';
import { useAppContext } from '../../share-components/AppContext';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTE_GENERAL_EXPEND_DETAILS, ROUTE_GENERAL_EXPEND_LIST } from '../../constants/routeConstants';
import * as Yup from 'yup';
import { Button, Card } from 'react-bootstrap';
import { customStorage, getCurrentJSTDate, getPayloadData, isNullOrEmptyString, isNullOrUndefinedObj, scrollToFormError } from '../../services/UtilityService';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import AlertDialog from '../../share-components/AlertDialog';
import ConfirmDialog from '../../share-components/ConfirmDialog';
import { editGeneralExpend, getGeneralExpendDetails } from '../../services/GeneralExpendService';
import { getLastLockedYearMonth } from '../../services/CommonService';
import FormTitle from '../../share-components/FormTitle';

// ? NEW IMPORTS
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import { SERVER_ERROR_MESSAGE, TOKEN_SESSION_KEY } from '../../constants/commonConstants';
import CalendarInput from '../../share-components/CalendarInput';

const getLastMonthDate = () => {
    const jst = getCurrentJSTDate();
    jst.setMonth(jst.getMonth() - 1); // subtract 1 month
    return jst;
};

const EditGeneralExpend = () => {
    const token = customStorage.getItem(TOKEN_SESSION_KEY);
    const currentUser = getPayloadData(token);

    const { generalExpendId } = useParams();
    const { setLoading, setShowApiErrorMessage, setRouteNaviData } = useAppContext();
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [formValue, setFormValue] = useState(null);
    const [isFirstLoaded, setIsFirstLoaded] = useState(false);
    const [editedGeneralExpendId, setEditedGeneralExpendId] = useState(null);
    const [lastLockedYearMonth, setLastLockedYearMonth] = useState(null);
    const [minDate, setMinDate] = useState(getLastMonthDate);
    const [generalExpendData, setGeneralExpendData] = useState(null);
    const navigate = useNavigate();

    const now = getCurrentJSTDate();
    const maxDate = now;
    const today = maxDate;

    const validateEditProcess = () => {
        const dateStr = generalExpendData?.expendedDate || '';
        const expendYear = dateStr.length >= 4 ? parseInt(dateStr.slice(0, 4), 10) : 0;
        const expendMonth = dateStr.length >= 6 ? parseInt(dateStr.slice(4, 6), 10) : 0;
        const isValidUser = isNullOrEmptyString(generalExpendData?.expendedById) || generalExpendData?.expendedById === currentUser?.userId;
        const isValidLockDate = isNullOrUndefinedObj(lastLockedYearMonth) || lastLockedYearMonth.toYear < expendYear ||
            (lastLockedYearMonth.toYear === expendYear && lastLockedYearMonth.toMonth < expendMonth);
        const isValid = isValidUser && isValidLockDate;
        if (!isValid) {
            navigate(`${ROUTE_GENERAL_EXPEND_DETAILS}/${generalExpendId}`);
        }
    }

    const initialLoad = async () => {
        setLoading(true);
        try {
            const generalExpendDetails = await getGeneralExpendDetails((parseInt(generalExpendId) || 0));
            if(generalExpendDetails?.expendedById !== currentUser?.userId){
                navigate(`${ROUTE_GENERAL_EXPEND_DETAILS}/${generalExpendId}`);
            }
            const expendedYear = parseInt(generalExpendDetails?.expendedDate?.slice(0, 4), 10);
            const expendedMonth = parseInt(generalExpendDetails?.expendedDate?.slice(4, 6), 10);

            const lastLockInfo = await getLastLockedYearMonth();
            setLastLockedYearMonth(lastLockInfo || null);
            if (lastLockInfo) {
                const minDate = new Date(lastLockInfo?.toYear, (parseInt(lastLockInfo?.toMonth) || 0), 1);
                setMinDate(minDate);
            } else {
                if (!!generalExpendDetails) {
                    const minDate = new Date(expendedYear, expendedMonth - 2, 1);
                    setMinDate(minDate);
                }
            }
            setGeneralExpendData(generalExpendDetails);
            setIsFirstLoaded(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setRouteNaviData("GENERAL EXPEND >> EDIT EXPEND");
        initialLoad();
    }, []);

    useEffect(() => {
        validateEditProcess();
    }, [generalExpendData]);

    const getOldDate = () => {
        const dateStr = generalExpendData?.expendedDate;
        if (!dateStr) {
            return '';
        }

        const year = parseInt(dateStr.substring(0, 4), 10);
        const month = parseInt(dateStr.substring(4, 6), 10) - 1; // month is 0-based in JS Date
        const day = parseInt(dateStr.substring(6, 8), 10);

        const dateObj = new Date(year, month, day);
        return dateObj;
    }

    const initialValues = {
        expendedReason: generalExpendData?.expendedReason || '',
        expendedPrice: generalExpendData?.expendedPrice || '',
        expendedDate: getOldDate(),
        comment: generalExpendData?.comment || ''
    };

    const validationSchema = Yup.object({
        expendedReason: Yup.string()
            .max(300, 'Expended Reason must not exceed 300 characters.')
            .required('Expended Reason is required.'),
        expendedPrice: Yup.number().nullable()
            .required('Expended Price is required.')
            .transform((value) => (Number.isNaN(value) ? null : value))
            .min(1, 'Expended Price must exceed 0.')
            .max(1000000, 'Expended Price must not exceed 1000000.'),
        expendedDate: Yup.date()
            .required('Expended Date is required.')
            .max(maxDate, 'Date cannot be in the future.')
            .min(minDate, `Date cannot be before ${format(minDate, 'yyyyMMdd')}`),
        comment: Yup.string().nullable()
            .max(300, 'Comment must not exceed 300 characters.')
    });

    const onSubmit = async (form) => {
        const payload = {
            generalExpendId,
            ...form,
            expendedDate: format(form.expendedDate, 'yyyyMMdd') // ? send formatted string
        };
        setFormValue(payload);
        setShowConfirmDialog(true);
    };

    const handleEditGeneralExpend = async () => {
        setShowConfirmDialog(false);
        setLoading(true);
        try {
            const editedId = await editGeneralExpend(formValue);
            setEditedGeneralExpendId(editedId);
            setShowSuccessDialog(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSuccessDialogClose = () => {
        setShowSuccessDialog(false);
        if ((parseInt(editedGeneralExpendId) || 0) > 0) {
            navigate(`${ROUTE_GENERAL_EXPEND_DETAILS}/${editedGeneralExpendId}`);
        } else {
            navigate(ROUTE_GENERAL_EXPEND_LIST);
        }
    };

    return (
        <div id='editGeneralExpendPage' style={{ paddingBottom: '50px' }}>
            <FormTitle title={"Edit Expend"} isCenter={true} />

            {isFirstLoaded && <>
                <Card style={{ padding: '10px', margin: '0 auto', maxWidth: '650px' }}>
                    <Card.Body className='pt-2'>
                        <div className="container">
                            <div className="row justify-content-center">
                                <div style={{ maxWidth: "550px" }}>
                                    <Formik
                                        initialValues={initialValues}
                                        validationSchema={validationSchema}
                                        onSubmit={onSubmit}>
                                        {({ setFieldValue, values }) => (
                                            <Form>
                                                {/* Expended Reason */}
                                                <div className="mb-1">
                                                    <label className='custom-label'>Expended Reason*</label>
                                                    <Field name="expendedReason" component="textarea" rows="3" className="w-100 customInputControl" autoComplete="off" />
                                                    <ErrorMessage name="expendedReason" component="div" className="formErrDiv text-danger" />
                                                </div>

                                                {/* Expended Price */}
                                                <div className="mb-1">
                                                    <label className='custom-label'>Expended Price*</label>
                                                    <Field
                                                        name="expendedPrice"
                                                        type="number"
                                                        className="w-100 customInputControl clearNumberSpin"
                                                        onKeyDown={(e) => {
                                                            if (["e", "-", ".", "+"].includes(e.key)) {
                                                                e.preventDefault();
                                                            }
                                                        }}
                                                        onWheel={(e) => e.target.blur()}
                                                    />
                                                    <ErrorMessage name="expendedPrice" component="div" className="formErrDiv text-danger" />
                                                </div>

                                                {/* ? Expended Date as DatePicker */}
                                                <div className="mb-1">
                                                    <label className='custom-label'>Expended Date(yyyyMMdd)*</label><br />
                                                    <DatePicker
                                                        selected={values.expendedDate}
                                                        onChange={(date) => setFieldValue("expendedDate", date)}
                                                        dateFormat="yyyy/MM/dd"
                                                        minDate={minDate}
                                                        maxDate={maxDate}
                                                        placeholderText="Select date"
                                                        customInput={<CalendarInput isReadOnly={true} />}  // 👈 here
                                                        dayClassName={(date) =>
                                                            date.getDate() === today.getDate() &&
                                                                date.getMonth() === today.getMonth() &&
                                                                date.getFullYear() === today.getFullYear()
                                                                ? "current-day"
                                                                : undefined
                                                        }
                                                    />
                                                    {/* <DatePicker
                                                        selected={values.expendedDate}
                                                        onChange={(date) => setFieldValue('expendedDate', date)}
                                                        dateFormat="yyyy/MM/dd"
                                                        className="w-100 customInputControl"
                                                        minDate={minDate}
                                                        maxDate={maxDate}
                                                        placeholderText="Select date"
                                                        aria-label="Calendar date picker"
                                                        aria-haspopup="dialog"
                                                        role="combobox"
                                                        autoComplete="off"
                                                        onKeyDown={(e) => e.preventDefault()} // Stops typing 
                                                        dayClassName={(date) => {
                                                            return (
                                                                date.getDate() === today.getDate() &&
                                                                date.getMonth() === today.getMonth() &&
                                                                date.getFullYear() === today.getFullYear()
                                                            )
                                                                ? "current-day"
                                                                : undefined;
                                                        }}
                                                    /> */}
                                                    <ErrorMessage name="expendedDate" component="div" className="formErrDiv text-danger" />
                                                </div>

                                                {/* Comment */}
                                                <div className="mb-1">
                                                    <label className='custom-label'>Comment</label>
                                                    <Field name="comment" component="textarea" rows="4" className="w-100 customInputControl" autoComplete="off" />
                                                    <ErrorMessage name="comment" component="div" className="formErrDiv text-danger" />
                                                </div>

                                                <div className='floating-bottom-btn-group mt-2'>
                                                    <div className='floating-bottom-btn-grid'>
                                                        <Button
                                                            className="w-100"
                                                            type="submit"
                                                            variant="primary"
                                                            onClick={scrollToFormError}
                                                        >
                                                            Save
                                                        </Button>
                                                    </div>
                                                </div>
                                            </Form>
                                        )}
                                    </Formik>
                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                <ConfirmDialog
                    dialogId='editGeneralExpendConfirmDialog'
                    showDialog={showConfirmDialog}
                    body='Are you sure to edit this expend?'
                    onConfirm={handleEditGeneralExpend}
                    onCancel={() => { setFormValue(null); setShowConfirmDialog(false); }}
                />
                <AlertDialog
                    dialogId={'successDialog'}
                    showDialog={showSuccessDialog}
                    body={'Your Expend has been successfully edited.'}
                    onConfirm={handleSuccessDialogClose}
                />
            </>}
        </div>
    );
};

export default EditGeneralExpend;
